import { FormEvent, useState } from "react";
import AdminResourceForm, { AdminField } from "../../components/admin/AdminResourceForm";
import EventImageUploads from "../../components/admin/EventImageUploads";
import { useAdminData } from "../../admin/AdminDataContext";
import { Event } from "../../types";

const emptyEvent = {
  title: "",
  eventType: "",
  date: "",
  location: "",
  description: "",
};

function normalizeGallery(urls?: string[]): string[] {
  const slots = [...(urls ?? [])];
  while (slots.length < 4) {
    slots.push("");
  }
  return slots.slice(0, 4);
}

function eventToFields(event: typeof emptyEvent): AdminField[] {
  return [
    { name: "title", label: "Title", value: event.title, required: true },
    { name: "eventType", label: "Event type", value: event.eventType, required: true },
    { name: "date", label: "Date", value: event.date },
    { name: "location", label: "Location", value: event.location },
    { name: "description", label: "Description", value: event.description, required: true },
  ];
}

function buildEventPayload(
  fields: AdminField[],
  featuredImageUrl: string,
  galleryImageUrls: string[]
): Omit<Event, "id"> {
  const map = Object.fromEntries(fields.map((f) => [f.name, f.value]));
  return {
    title: map.title,
    eventType: map.eventType,
    date: map.date || undefined,
    location: map.location || undefined,
    description: map.description,
    heroImageUrl: featuredImageUrl || undefined,
    galleryImageUrls: galleryImageUrls.filter(Boolean),
  };
}

function AdminEvents() {
  const { events, loading, error, addEvent, updateEvent, removeEvent } = useAdminData();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fields, setFields] = useState<AdminField[]>(eventToFields(emptyEvent));
  const [featuredImageUrl, setFeaturedImageUrl] = useState("");
  const [galleryImageUrls, setGalleryImageUrls] = useState<string[]>(normalizeGallery());
  const [formError, setFormError] = useState<string | null>(null);

  const resetImages = (event?: Event) => {
    setFeaturedImageUrl(event?.heroImageUrl ?? "");
    setGalleryImageUrls(normalizeGallery(event?.galleryImageUrls));
  };

  const openCreate = () => {
    setEditingId(null);
    setFields(eventToFields(emptyEvent));
    resetImages();
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (event: Event) => {
    setEditingId(event.id);
    setFields(
      eventToFields({
        title: event.title,
        eventType: event.eventType,
        date: event.date ?? "",
        location: event.location ?? "",
        description: event.description ?? "",
      })
    );
    resetImages(event);
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormError(null);
  };

  const handleChange = (name: string, value: string) => {
    setFormError(null);
    setFields((prev) => prev.map((f) => (f.name === name ? { ...f, value } : f)));
  };

  const handleGalleryChange = (index: number, url: string) => {
    setFormError(null);
    setGalleryImageUrls((prev) => {
      const next = [...prev];
      next[index] = url;
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = buildEventPayload(fields, featuredImageUrl, galleryImageUrls);
    if (!payload.title.trim()) {
      setFormError("Title is required.");
      return;
    }
    if (!payload.eventType.trim()) {
      setFormError("Event type is required.");
      return;
    }
    if (!payload.description?.trim()) {
      setFormError("Description is required.");
      return;
    }
    try {
      if (editingId) {
        await updateEvent(editingId, payload);
      } else {
        await addEvent(payload);
      }
      closeForm();
    } catch {
      setFormError("Failed to save event. Check your connection and try again.");
    }
  };

  const handleDelete = async (event: Event) => {
    if (window.confirm(`Delete "${event.title}"?`)) {
      try {
        await removeEvent(event.id);
      } catch {
        setFormError("Failed to delete event.");
      }
    }
  };

  if (loading) return <p className="body-text">Loading events...</p>;
  if (error) return <p className="admin-error">Error: {error}</p>;

  return (
    <div className="admin-card">
      <div className="admin-card__header">
        <h1 className="h1-text">Events</h1>
        {!showForm && (
          <button type="button" className="admin-btn admin-btn--primary" onClick={openCreate}>
            Add event
          </button>
        )}
      </div>

      {showForm && (
        <>
          {formError && <p className="admin-error">{formError}</p>}
          <AdminResourceForm
            title={editingId ? "Edit event" : "Add event"}
            fields={fields}
            onChange={handleChange}
            onSubmit={handleSubmit}
            onCancel={closeForm}
            submitLabel={editingId ? "Save changes" : "Add event"}
          />
          <EventImageUploads
            featuredImageUrl={featuredImageUrl}
            galleryImageUrls={galleryImageUrls}
            onFeaturedChange={setFeaturedImageUrl}
            onGalleryChange={handleGalleryChange}
          />
        </>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Type</th>
            <th>Date</th>
            <th>Photos</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.id}>
              <td>{event.title}</td>
              <td>{event.eventType}</td>
              <td>{event.date ?? "—"}</td>
              <td>{1 + (event.galleryImageUrls?.length ?? 0)}</td>
              <td>
                <div className="admin-table-actions">
                  <button type="button" className="admin-btn" onClick={() => openEdit(event)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn--danger"
                    onClick={() => handleDelete(event)}
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminEvents;
